import { DistributiveOmit, objectMap } from '@direct-frontend/stdlib';

import { FormActionsMap, FormInstance } from '../../form/types';
import { assertFormInitialized } from '../../form/utils/assertFormInitialized';
import { assertFormNotSubmitting } from '../../form/utils/assertFormNotSubmitting';
import { getFormStoreSingleton } from '../../store/store';
import { BaseValidationError } from '../../types';

import { BaseArrayField } from '../createArrayField';
import { BaseObjectField } from '../createObjectField';
import { BasePrimitiveField } from '../createPrimitiveField';
import { BaseFields, BaseFieldInstance, $canSetWhileSubmitting, SetValueOptions } from '../types';
import { BaseUnionField } from '../createUnionField';
import { $internals } from '../../form/types';
import { getOrCreateFrozenInstance } from '../../form/frozenInstancesContext';

/** Привязывает асинхронные экшены поля с поддержкой takeLatest */
export function bindFieldAsyncActions<
    Field extends BasePrimitiveField | BaseObjectField | BaseArrayField | BaseUnionField,
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    field: Field,
    fieldId: string,
    getFieldInstance: (
        frozenFormInstance?: FormInstance<
            FormName,
            FormFields,
            FormValidationError,
            FormContextInput,
            FormContext,
            FormActions,
            FormSubmitArg,
            FormSubmitReturnValue
        >,
        options?: {
            setterOptions?: SetValueOptions;
        }
    ) => DistributiveOmit<BaseFieldInstance, 'asyncActions'>,
    /**
     * Опции сеттера для предоставления возможности запускать экшен во время сабмита
     * Актуально для асинхронных экшенов дочерних полей текущего поля
     */
    setterOptions?: SetValueOptions
) {
    if (!('asyncActions' in field) || !field.asyncActions) {
        return;
    }

    const { getFieldState, updateFieldAsyncActionsMeta } = getFormStoreSingleton(form.name);

    return objectMap(field.asyncActions, (asyncAction, actionName) => (...args: unknown[]) => {
        const frozenFormInitializationId = form[$internals].formInitializationId;
        const frozenFormInstance = getOrCreateFrozenInstance(frozenFormInitializationId, form);

        const frozenFieldInstance = getFieldInstance(frozenFormInstance, {
            setterOptions: { [$canSetWhileSubmitting]: true },
        });

        assertFormInitialized({
            formName: frozenFieldInstance.form.name,
            formId: frozenFieldInstance.form.id,
            formInitializationId: frozenFormInitializationId,
        });
        assertFormNotSubmitting(
            `Can not edit ${field.type} field value, while form is submitting`,
            {
                formName: frozenFieldInstance.form.name,
                formId: frozenFieldInstance.form.id,
                formInitializationId: frozenFormInitializationId,
            },
            fieldId,
            setterOptions
        );

        // Создаем AbortController для возможности отмены экшена
        const controller = new AbortController();

        const fieldState = getFieldState({ formInitializationId: frozenFormInitializationId, fieldId });

        // takeLatest: если экшен с таким именем уже запущен, абортим его
        const existingActionMeta = fieldState?.asyncActionsMeta?.[actionName];
        if (existingActionMeta) {
            existingActionMeta.controller.abort();
        }

        // Асинхронный экшен - отслеживаем промис
        const actionPromise = asyncAction(frozenFieldInstance, controller.signal, ...args).finally(() => {
            // Удаляем метаданные экшена после завершения
            const state = getFieldState({ formInitializationId: frozenFormInitializationId, fieldId });
            if (state?.asyncActionsMeta?.[actionName]) {
                const newAsyncActionsMeta = { ...state.asyncActionsMeta };
                delete newAsyncActionsMeta[actionName];
                updateFieldAsyncActionsMeta({
                    formInitializationId: frozenFormInitializationId,
                    fieldId,
                    asyncActionsMeta: newAsyncActionsMeta,
                });
            }
        });

        // Добавляем метаданные экшена
        updateFieldAsyncActionsMeta({
            formInitializationId: frozenFormInitializationId,
            fieldId,
            asyncActionsMeta: {
                [actionName]: {
                    controller,
                    promise: actionPromise,
                },
            },
        });

        return actionPromise;
    });
}
